/**
 * Schema Compare Engine Utilities
 *
 * Utility functions for working with database engines in schema compare.
 */

import type { DatabaseEngineId } from '../../../../core/engines';
import type { ActiveConnection } from '../../../connections/models/connectionTypes';
import { useConnectionStore } from '../../../connections/store/connectionStore';

/**
 * Get engine ID from a connection ID
 * Checks both saved connections and active connections (for browser mode support)
 */
export function getEngineFromConnectionId(
  connectionId: string | undefined,
  connections: Array<{ id: string; engine?: string }>,
  activeConnections?: ActiveConnection[]
): DatabaseEngineId | undefined {
  if (!connectionId) return undefined;

  // Check saved connections first
  const savedConn = connections.find((c) => c.id === connectionId);
  if (savedConn) {
    const engine = savedConn.engine ?? 'mssql';
    if (engine === 'mssql' || engine === 'pgsql' || engine === 'mysql' || engine === 'sqlite' || engine === 'oracle' || engine === 'mock') {
      return engine;
    }
    return 'mssql';
  }

  // Fallback to active connections (for browser mode SQLite connections)
  if (activeConnections) {
    const activeConn = activeConnections.find((c) => c.id === connectionId);
    if (activeConn) {
      const engine = activeConn.connection.engine ?? 'mssql';
      if (engine === 'mssql' || engine === 'pgsql' || engine === 'mysql' || engine === 'sqlite' || engine === 'oracle' || engine === 'mock') {
        return engine;
      }
      return 'mssql';
    }
  }

  return undefined;
}

/**
 * Same lookup, reading the live connection store instead of taking the lists
 * as arguments — for non-React callers that only hold a connection id.
 *
 * W5.2 fix round 1 (F-3): `createDefaultSchemaCompareContent` bakes the
 * per-engine Standard ignore list into a fresh tab's options, but its two
 * non-dialog callers (`workspaceStore.createDefaultContent` for
 * navigation-created tabs, `tabDeserializer` for sessions saved before options
 * were persisted) were passing no engine at all, so the baseline read back as
 * 'custom' once the tab resolved its real engine. Both have a connection id in
 * hand; this is how they turn it into an engine.
 *
 * Returns `undefined` when the id is missing or the connection isn't in the
 * store (e.g. a session restored before connections finished loading), which
 * the factory handles by writing no ignore list at all.
 */
export function resolveEngineForConnectionId(
  connectionId: string | undefined,
): DatabaseEngineId | undefined {
  if (!connectionId) return undefined;
  const { connections, activeConnections } = useConnectionStore.getState();
  return getEngineFromConnectionId(connectionId, connections, activeConnections);
}
